import _ from 'lodash'
import styled from 'styled-components'
import { WordWraps } from 'common-components'

import { getCIESExceptionNotes, getCIESExplanationNotes } from 'core/service/rider/benefits/exception-notes'

import RiderBenefitSummary from './rider-benefit-summary'
import RiderWithBenefitModal from './rider-benefit-modal'
import { formatNumber } from 'core/service/lib/number-format'

import MESSAGES from 'core/data-model/constants/bi-messages'

const UnderlineStyled = styled.div`
  text-decoration: underline;
  font-weight: bold;
  margin-top: 10px;
  margin-bottom: 10px;
`

const TiltleStyled = styled.div`
  font-weight: bold;
  margin-top: 10px;
  margin-bottom: 10px;
`

export const findDataOnListByRiderCode = (contents, planCode) => {
  return contents.find((content) => content.planCode === planCode)
    ? contents.find((content) => content.planCode === planCode).value
    : '-'
}
const renderSubItem = (
  data,
  {
    labelFormat = '', // 'dot', 'parentheses', 'custom'
    customPrefix = '',
    customSuffix = '',
    indent = '20px',
  } = {}
) => {
  return data.map((dataRow, index) => {
    let label = ''

    switch (labelFormat) {
      case 'parentheses':
        label = `(${index + 1})`
        break
      case 'dot':
        label = `${index + 1}.`
        break
      case 'custom':
        label = `${customPrefix}${index + 1}${customSuffix}`
        break
      default:
        label = `${index + 1})`
    }

    return (
      <div className="list-cell" key={`item-${index}`} style={{ marginLeft: indent }}>
        <span style={{ display: 'inline-block', marginRight: '5px' }}>{label}</span>
        <span dangerouslySetInnerHTML={{ __html: dataRow }} />
      </div>
    )
  })
}
const renderExceptionNotes = () => {
  const exceptionNotes = getCIESExceptionNotes()
  const [
    normalConditionSubject,
    [
      extendExceptionDescTitle,
      firstExceptionTitle,
      [
        firstExceptionDetail,
        firstExceptionList,
        secondExceptionTitle,
        secondExceptionList,
        thirdExceptionTitle,
        thirdExceptionList,
      ],
    ],
    warningTitle,
    [warningDescTitle, warningDescList],
    noteText,
    importantConditionTitle,
    [importantConditionDetail, subConditionDetail],
  ] = exceptionNotes.exceptions
  return (
    <div id="cies-exception">
      <p></p>
      <p>
        <h3>{normalConditionSubject} </h3>
      </p>

      <p>
        <u>{extendExceptionDescTitle}</u>
      </p>
      <p>{firstExceptionTitle}</p>

      <div style={{ marginBottom: '20px' }}>
        <TiltleStyled>1. {firstExceptionDetail}</TiltleStyled>
        {renderSubItem(firstExceptionList, {})}
        <TiltleStyled>2. {secondExceptionTitle}</TiltleStyled>
        {renderSubItem(secondExceptionList, {})}
        <TiltleStyled>3. {thirdExceptionTitle}</TiltleStyled>
        {renderSubItem(thirdExceptionList, {})}
      </div>
      <p>
        <h3>{warningTitle}</h3>
      </p>
      <p>{warningDescTitle}</p>
      <p>{renderSubItem(warningDescList, { labelFormat: 'dot', indent: 0 })}</p>

      <p>{noteText}</p>
      <p>
        <h3>{importantConditionTitle}</h3>
      </p>
      <p>
        <div>{importantConditionDetail}</div>
        <div>{subConditionDetail}</div>
      </p>
    </div>
  )
}

const buildSubItems = (blocks, prefix, riderCodes) =>
  riderCodes.map((riderCode, index) => ({
    prefix,
    index: index + 1,
    title: blocks[index * 2],
    details: blocks[index * 2 + 1],
    riderCode,
  }))

const formatAmount = (amount) => (typeof amount === 'number' ? `${formatNumber(amount)} บาท` : 'ไม่คุ้มครอง')

const ExplanationSubItem = ({ prefix, index, title, details, amount, boldFlags = [] }) => (
  <div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '18px',
        paddingLeft: '18px', //  indent ข้อย่อย เช่น 1.1, 1.2
      }}
    >
      <strong>{`${prefix}${index} ${title}`}</strong>
      <span style={{ fontWeight: 'bold' }}>{amount}</span>
    </div>

    <div style={{ paddingLeft: '42px' }}>
      {details.map((line, i) => {
        const isBold = boldFlags[i] === true
        return (
          <div
            key={i}
            style={{
              fontWeight: isBold ? 'bold' : 'normal',
            }}
          >
            {line}
          </div>
        )
      })}
    </div>
  </div>
)
const resolveMainRiderCodes = (selectedPlanGroup) => {
  const mainCandidates = ['CIES3', 'CIES4']
  const mainCode = mainCandidates.find((code) => selectedPlanGroup?.[code])
  return [mainCode || 'CIES4', 'CIESCB', 'CIESJN']
}

const renderExplanationNotes = (selectedPlanGroup) => {
  const explanations = getCIESExplanationNotes().explanations
  const [mainTitle, mainBlocks, riderTitle, riderBlocks] = explanations
  const resolvedMainCodes = resolveMainRiderCodes(selectedPlanGroup)
  const mainItems = buildSubItems(mainBlocks, '1.', resolvedMainCodes)
  const subItems = buildSubItems(riderBlocks, '2.', ['CIESPE', 'CIESEI', 'CIESSC'])

  return (
    <div id="cies-explanation" style={{ marginBottom: '20px' }}>
      <TiltleStyled>1. {mainTitle}</TiltleStyled>
      {mainItems.map((item) => {
        const is1_2 = item.index === 2 // 1.2 only
        const boldFlagsForItem = is1_2 ? [true, false, false] : []

        return (
          <ExplanationSubItem
            key={item.riderCode}
            prefix={item.prefix}
            index={item.index}
            title={item.title}
            details={item.details}
            amount={formatAmount(selectedPlanGroup?.[item.riderCode]?.riderSumAssured)}
            boldFlags={boldFlagsForItem}
          />
        )
      })}
      <TiltleStyled>2. {riderTitle}</TiltleStyled>
      {subItems.map((item) => (
        <ExplanationSubItem
          key={item.riderCode}
          prefix={item.prefix}
          index={item.index}
          title={item.title}
          details={item.details}
          amount={formatAmount(selectedPlanGroup?.[item.riderCode]?.riderSumAssured)}
          boldFlags={[]}
        />
      ))}
    </div>
  )
}

const renderDisease = (diseaseGroups, riderCode) => {
  return diseaseGroups.map(({ id, title, groups }) => (
    <div key={id} style={{ marginBottom: '18px' }}>
      <h3>{title}</h3>

      {groups.map(({ group, label, description, items }) => {
        if (group === 'juvenile' && riderCode !== 'CIES3') return null

        return (
          <div key={group}>
            <UnderlineStyled>{label}</UnderlineStyled>
            {description && <p style={{ marginBottom: '20px' }}>{description}</p>}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                alignItems: 'start',
                gap: '26px',
              }}
            >
              {splitIntoColumns(items, 2).map((columnItems, colIndex) => (
                <div key={colIndex}>
                  <ol style={{ paddingLeft: '16px' }} start={getStartIndex(items.length, colIndex, 2)}>
                    {columnItems.map((item, index) => (
                      <li key={index} style={{ marginBottom: '10px' }}>
                        {item}
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  ))
}

const splitIntoColumns = (items, numCols) => {
  const itemsPerCol = Math.ceil(items.length / numCols)
  return Array.from({ length: numCols }, (_, colIndex) =>
    items.slice(colIndex * itemsPerCol, (colIndex + 1) * itemsPerCol)
  )
}

const getStartIndex = (totalItems, columnIndex, columnCount) => {
  const itemsPerColumn = Math.ceil(totalItems / columnCount)
  return columnIndex * itemsPerColumn + 1
}

const renderDiseaseGroups = (diseaseGroups, riderCode) => {
  const noteMessage = _.get(_.find(diseaseGroups, 'note'), 'note') || ''
  return (
    <div>
      {renderDisease(diseaseGroups, riderCode)}
      {noteMessage}
    </div>
  )
}

const RenderCIESRiderBenefitsHeadTitle = (props) => {
  const { headTitle } = props
  return <h3>{headTitle}</h3>
}

const renderDisclaimer = () => {
  return (
    <div>
      <u>หมายเหตุ</u>: * {MESSAGES.DISCLAIMER_CIES_RIDER_PREMIUM}
    </div>
  )
}

const CIESRiderBenefit = (props) => {
  const { rider, riderBenefitData, insureAge } = props
  const title = `${rider.description} (${rider.name})`
  const planCode = _.get(rider, 'selectedPlan.planCode', '')
  const selectedPlanGroup = rider.selectedPlanGroup
  const diseaseGroups = riderBenefitData?.diseaseGroups
  const { headTitle } = riderBenefitData

  return (
    <RiderWithBenefitModal id="cies-rider-benefit" className="rider mexmea-plus-rider-rider" title={title}>
      <RiderBenefitSummary rider={rider} title={title} readOnly={true} />
      <RenderCIESRiderBenefitsHeadTitle headTitle={headTitle} />
      {renderExplanationNotes(selectedPlanGroup)}
      {renderDisclaimer()}
      {renderExceptionNotes()}
      {renderDiseaseGroups(diseaseGroups, planCode)}
    </RiderWithBenefitModal>
  )
}

export default CIESRiderBenefit
